import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Container,
  Box,
  Typography,
  Grid,
  IconButton,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  keyframes,
  CircularProgress,
  Card,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Gavel,
  Description,
  CheckCircle,
  Message,
  VideoCall,
  Logout,
  Settings,
  ChatBubble,
  Search,
  CalendarToday,
  Menu as MenuIcon,
  Close,
} from '@mui/icons-material';
import { logout } from '../../store/slices/authSlice';
import clientService from '../../services/clientService';
import { useTranslation } from '../../i18n';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { axelionColors } from '../../theme/axelionTheme';
import NotificationCenter from '../../components/UI/NotificationCenter';
import QuickAIChat from '../../components/QuickAIChat';
import {
  Person as PersonIcon,
  TrendingFlat,
} from '@mui/icons-material';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * MaslaXat Premium Dashboard
 * Elegant, minimalist design with gold accents
 */
const DashboardPageGlass = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:767px)');
  const isTablet = useMediaQuery('(min-width:768px) and (max-width:1023px)');

  const [stats, setStats] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, consultationsData] = await Promise.all([
        clientService.dashboard.getStats(),
        clientService.dashboard.getUpcomingConsultations(),
      ]);

      setStats(statsData);
      setConsultations(consultationsData);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleJoinConsultation = async (consultation) => {
    try {
      await clientService.consultations.joinConsultation(consultation.id);
      if (consultation.type === 'video') {
        navigate(`/consultations/video/${consultation.id}`);
      } else {
        navigate(`/consultations/chat/${consultation.id}`);
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const quickActions = [
    {
      icon: <ChatBubble sx={{ fontSize: 28 }} />,
      labelKey: 'askAI',
      descKey: 'AI консультант',
      onClick: () => navigate('/ai-chat'),
      primary: true,
    },
    {
      icon: <Search sx={{ fontSize: 28 }} />,
      labelKey: 'findLawyer',
      descKey: 'Поиск специалиста',
      onClick: () => navigate('/lawyers'),
    },
    {
      icon: <CalendarToday sx={{ fontSize: 28 }} />,
      labelKey: 'myConsultations',
      descKey: 'Ваши записи',
      onClick: () => navigate('/consultations'),
    },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: axelionColors.bgCream,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            border: `2px solid ${axelionColors.borderLight}`,
            borderTopColor: axelionColors.gold,
            animation: 'spin 1s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 300,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: axelionColors.textMuted,
          }}
        >
          {t('common.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: axelionColors.bgCream,
        pb: 6,
      }}
    >
      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: axelionColors.bgLight,
            borderRight: `1px solid ${axelionColors.borderLight}`,
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 300,
                fontSize: '1.25rem',
                letterSpacing: '0.2em',
                color: axelionColors.gold,
                textTransform: 'uppercase',
              }}
            >
              MaslaXat
            </Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)}>
              <Close sx={{ color: axelionColors.textDark }} />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, bgcolor: axelionColors.bgWarm, borderRadius: '8px' }}>
            <Avatar
              sx={{
                width: 44,
                height: 44,
                bgcolor: axelionColors.bgBeige,
                color: axelionColors.gold,
                border: `2px solid ${axelionColors.gold}`,
              }}
            >
              {user?.name?.charAt(0) || 'К'}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', color: axelionColors.textDark }}>
                {user?.name || t('login.client')}
              </Typography>
              <Chip
                label={t('login.client').toUpperCase()}
                size="small"
                sx={{
                  bgcolor: axelionColors.accentLight,
                  color: axelionColors.gold,
                  fontWeight: 500,
                  fontSize: '0.6rem',
                  height: 20,
                  borderRadius: '8px',
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ borderColor: axelionColors.borderLight, mb: 2 }} />

          <List>
            {[
              { icon: <ChatBubble />, label: t('dashboard.askAI'), onClick: () => navigate('/ai-chat') },
              { icon: <Search />, label: t('dashboard.findLawyer'), onClick: () => navigate('/lawyers') },
              { icon: <CalendarToday />, label: t('dashboard.viewAll'), onClick: () => navigate('/consultations') },
              { icon: <Description />, label: t('nav.documents'), onClick: () => navigate('/documents') },
              { icon: <Settings />, label: t('nav.settings') || 'Настройки', onClick: () => navigate('/settings') },
            ].map((item, index) => (
              <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => { item.onClick(); setMobileMenuOpen(false); }}
                  sx={{
                    borderRadius: '8px',
                    '&:hover': { bgcolor: axelionColors.bgWarm },
                  }}
                >
                  <ListItemIcon sx={{ color: axelionColors.gold, minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.9rem', color: axelionColors.textDark }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Divider sx={{ borderColor: axelionColors.borderLight, my: 2 }} />

          <Box sx={{ px: 1 }}>
            <LanguageSwitcher variant="buttons" />
          </Box>

          <Button
            fullWidth
            startIcon={<Logout sx={{ fontSize: 18 }} />}
            onClick={handleLogout}
            sx={{
              mt: 2,
              bgcolor: axelionColors.textDark,
              color: '#FFFFFF',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontWeight: 500,
              fontSize: '0.75rem',
              py: 1.5,
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#2D2D2D', boxShadow: 'none' },
            }}
          >
            {t('nav.logout')}
          </Button>
        </Box>
      </Drawer>

      {/* Header */}
      <Box
        sx={{
          backgroundColor: axelionColors.bgLight,
          borderBottom: `1px solid ${axelionColors.borderLight}`,
          py: { xs: 2, md: 3 },
          px: { xs: 1, md: 2 },
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 3 } }}>
              {/* Mobile hamburger */}
              {isMobile && (
                <IconButton
                  onClick={() => setMobileMenuOpen(true)}
                  sx={{
                    border: `1px solid ${axelionColors.borderLight}`,
                    borderRadius: '8px',
                    color: axelionColors.textDark,
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}

              {/* Logo */}
              <Typography
                sx={{
                  fontWeight: 300,
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  letterSpacing: '0.2em',
                  color: axelionColors.gold,
                  textTransform: 'uppercase',
                }}
              >
                M
              </Typography>
              {!isMobile && (
                <>
                  <Box
                    sx={{
                      width: '1px',
                      height: '40px',
                      backgroundColor: axelionColors.borderLight,
                    }}
                  />
                  <Avatar
                    sx={{
                      width: { xs: 40, md: 56 },
                      height: { xs: 40, md: 56 },
                      backgroundColor: axelionColors.bgBeige,
                      color: axelionColors.gold,
                      fontSize: '1.25rem',
                      fontWeight: 300,
                      border: `2px solid ${axelionColors.gold}`,
                    }}
                  >
                    {user?.name?.charAt(0) || 'К'}
                  </Avatar>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: '1.1rem',
                          color: axelionColors.textDark,
                          letterSpacing: '0.02em',
                        }}
                      >
                        {user?.name || t('login.client')}
                      </Typography>
                      <Chip
                        label={t('login.client').toUpperCase()}
                        size="small"
                        sx={{
                          backgroundColor: axelionColors.accentLight,
                          color: axelionColors.gold,
                          fontWeight: 500,
                          fontSize: '0.65rem',
                          letterSpacing: '0.1em',
                          height: 22,
                          borderRadius: '8px',
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        color: axelionColors.textMuted,
                        fontSize: '0.8rem',
                        mt: 0.5,
                      }}
                    >
                      {t('dashboard.welcome')} eMaslaXat
                    </Typography>
                  </Box>
                </>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: { xs: 0.5, md: 1.5 }, alignItems: 'center' }}>
              {!isMobile && <LanguageSwitcher variant="dropdown" />}
              <NotificationCenter />
              {!isMobile && (
                <>
                  <IconButton
                    sx={{
                      backgroundColor: axelionColors.bgLight,
                      border: `1px solid ${axelionColors.borderLight}`,
                      borderRadius: '8px',
                      color: axelionColors.textSecondary,
                      '&:hover': {
                        backgroundColor: axelionColors.bgWarm,
                        borderColor: axelionColors.gold,
                        color: axelionColors.gold,
                      },
                    }}
                    onClick={() => navigate('/settings')}
                  >
                    <Settings sx={{ fontSize: 20 }} />
                  </IconButton>
                  <Button
                    startIcon={<Logout sx={{ fontSize: 18 }} />}
                    onClick={handleLogout}
                    sx={{
                      backgroundColor: axelionColors.textDark,
                      color: '#FFFFFF',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      px: 2.5,
                      py: 1,
                      borderRadius: '8px',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: '#2D2D2D',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {t('nav.logout')}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 } }}>
        {/* Mobile Welcome */}
        {isMobile && (
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '1rem',
                color: axelionColors.textDark,
              }}
            >
              {t('dashboard.welcome')}, {user?.name?.split(' ')[0] || t('login.client')}
            </Typography>
            <Typography sx={{ color: axelionColors.textMuted, fontSize: '0.8rem' }}>
              eMaslaXat
            </Typography>
          </Box>
        )}

        {/* Stats Cards */}
        <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mb: { xs: 2, md: 4 } }}>
          {[
            {
              icon: <Gavel sx={{ fontSize: 28 }} />,
              labelKey: 'activeConsultations',
              value: stats?.activeConsultations || 0,
            },
            {
              icon: <Description sx={{ fontSize: 28 }} />,
              labelKey: 'documents',
              value: stats?.documents || 0,
            },
            {
              icon: <CheckCircle sx={{ fontSize: 28 }} />,
              labelKey: 'completedConsultations',
              value: stats?.completedConsultations || 0,
            },
            {
              icon: <ChatBubble sx={{ fontSize: 28 }} />,
              labelKey: 'aiChats',
              value: stats?.aiChats || 0,
            },
          ].map((stat, index) => (
            <Grid
              item
              xs={6}
              sm={6}
              lg={3}
              key={index}
              sx={{
                animation: `${fadeInUp} 0.5s ease-out`,
                animationDelay: `${index * 0.1}s`,
                animationFillMode: 'both',
              }}
            >
              <Card
                sx={{
                  backgroundColor: axelionColors.bgLight,
                  border: `1px solid ${axelionColors.borderLight}`,
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(26, 26, 26, 0.04)',
                  p: { xs: 2, md: 3 },
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(26, 26, 26, 0.08)',
                    borderColor: axelionColors.gold,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1.5 : 2.5 }}>
                  <Box
                    sx={{
                      width: { xs: 40, md: 56 },
                      height: { xs: 40, md: 56 },
                      borderRadius: '8px',
                      backgroundColor: axelionColors.accentLight,
                      color: axelionColors.gold,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& .MuiSvgIcon-root': { fontSize: { xs: 22, md: 28 } },
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        color: axelionColors.textMuted,
                        fontSize: { xs: '0.6rem', md: '0.7rem' },
                        fontWeight: 500,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        mb: 0.5,
                      }}
                    >
                      {stat.labelKey === 'documents' ? t('nav.documents') :
                       t(`dashboard.${stat.labelKey}`)}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 300,
                        fontSize: { xs: '1.5rem', md: '2rem' },
                        color: axelionColors.textDark,
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {stat.value}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Onboarding Progress — shown for new users */}
        {stats && (stats.activeConsultations || 0) + (stats.completedConsultations || 0) === 0 && (
          <Card
            sx={{
              mb: { xs: 2, md: 3 },
              backgroundColor: axelionColors.bgLight,
              border: `1px solid ${axelionColors.borderLight}`,
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(26, 26, 26, 0.04)',
              p: { xs: 2, md: 3 },
              animation: `${fadeInUp} 0.5s ease-out`,
              animationDelay: '0.4s',
              animationFillMode: 'both',
            }}
          >
            <Typography
              sx={{
                fontWeight: 300,
                fontSize: '0.8rem',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: axelionColors.textDark,
                mb: 2,
              }}
            >
              {t('dashboard.welcome')} — {t('common.next')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1.5, md: 2 }, alignItems: { md: 'center' } }}>
              {[
                {
                  step: 1,
                  icon: <PersonIcon sx={{ fontSize: 22 }} />,
                  label: 'Заполните профиль',
                  done: !!user?.phone || !!user?.avatar,
                  onClick: () => navigate('/profile'),
                },
                {
                  step: 2,
                  icon: <ChatBubble sx={{ fontSize: 22 }} />,
                  label: 'Спросите AI',
                  done: (stats?.aiChats || 0) > 0,
                  onClick: () => navigate('/ai-chat'),
                },
                {
                  step: 3,
                  icon: <Gavel sx={{ fontSize: 22 }} />,
                  label: 'Запишитесь к юристу',
                  done: false,
                  onClick: () => navigate('/lawyers'),
                },
              ].map((item, idx, arr) => (
                <React.Fragment key={item.step}>
                  <Box
                    onClick={item.onClick}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      flex: 1,
                      cursor: 'pointer',
                      p: 1.5,
                      borderRadius: '8px',
                      bgcolor: item.done ? axelionColors.accentLight : axelionColors.bgWarm,
                      border: `1px solid ${item.done ? axelionColors.gold : axelionColors.borderLight}`,
                      transition: 'all 0.2s',
                      opacity: item.done ? 0.7 : 1,
                      '&:hover': {
                        borderColor: axelionColors.gold,
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: item.done ? axelionColors.gold : axelionColors.bgBeige,
                        color: item.done ? '#FFFFFF' : axelionColors.gold,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {item.done ? <CheckCircle sx={{ fontSize: 20 }} /> : item.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.65rem', color: axelionColors.textMuted, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        {`Шаг ${item.step}`}
                      </Typography>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: item.done ? 400 : 500, color: item.done ? axelionColors.textMuted : axelionColors.textDark, textDecoration: item.done ? 'line-through' : 'none' }}>
                        {item.label}
                      </Typography>
                    </Box>
                  </Box>
                  {idx < arr.length - 1 && !isMobile && (
                    <TrendingFlat sx={{ color: axelionColors.borderLight, fontSize: 24, flexShrink: 0 }} />
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Card>
        )}

        <Grid container spacing={{ xs: 2, md: 3 }}>
          {/* Quick AI Chat */}
          <Grid item xs={12} lg={8} sx={{ order: { xs: 2, lg: 1 } }}>
            <QuickAIChat />
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12} lg={4} sx={{ order: { xs: 1, lg: 2 } }}>
            <Card
              sx={{
                backgroundColor: axelionColors.bgLight,
                border: `1px solid ${axelionColors.borderLight}`,
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(26, 26, 26, 0.04)',
                p: 3,
                height: '100%',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 300,
                  fontSize: '0.8rem',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: axelionColors.textDark,
                  mb: 3,
                }}
              >
                {t('dashboard.quickActions')}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {quickActions.map((action, index) => (
                  <Card
                    key={index}
                    onClick={action.onClick}
                    sx={{
                      backgroundColor: action.primary ? axelionColors.gold : axelionColors.bgWarm,
                      border: action.primary ? `1px solid ${axelionColors.goldDark}` : `1px solid ${axelionColors.borderLight}`,
                      borderRadius: '8px',
                      p: action.primary ? 2.5 : 2,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      animation: `${fadeInUp} 0.5s ease-out`,
                      animationDelay: `${(index + 4) * 0.1}s`,
                      animationFillMode: 'both',
                      boxShadow: action.primary ? '0 4px 16px rgba(184, 149, 110, 0.3)' : 'none',
                      '&:hover': {
                        backgroundColor: action.primary ? axelionColors.goldDark : axelionColors.bgLight,
                        borderColor: axelionColors.gold,
                        transform: action.primary ? 'translateY(-2px)' : 'translateX(4px)',
                        boxShadow: action.primary ? '0 6px 20px rgba(184, 149, 110, 0.4)' : 'none',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '8px',
                          backgroundColor: action.primary ? 'rgba(255,255,255,0.2)' : axelionColors.accentLight,
                          color: action.primary ? '#FFFFFF' : axelionColors.gold,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {action.icon}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: action.primary ? 600 : 500,
                            fontSize: action.primary ? '1rem' : '0.9rem',
                            color: action.primary ? '#FFFFFF' : axelionColors.textDark,
                          }}
                        >
                          {t(`dashboard.${action.labelKey}`)}
                        </Typography>
                        <Typography
                          sx={{
                            color: action.primary ? 'rgba(255,255,255,0.8)' : axelionColors.textMuted,
                            fontSize: '0.75rem',
                          }}
                        >
                          {action.descKey}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Box>
            </Card>
          </Grid>

          {/* Upcoming Consultations */}
          <Grid item xs={12} sx={{ order: 3 }}>
            <Card
              sx={{
                backgroundColor: axelionColors.bgLight,
                border: `1px solid ${axelionColors.borderLight}`,
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(26, 26, 26, 0.04)',
                p: 3,
                animation: `${fadeInUp} 0.5s ease-out`,
                animationDelay: '0.6s',
                animationFillMode: 'both',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography
                  sx={{
                    fontWeight: 300,
                    fontSize: '0.8rem',
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    color: axelionColors.textDark,
                  }}
                >
                  {t('consultations.upcoming')}
                </Typography>
                <Button
                  startIcon={<CalendarToday sx={{ fontSize: 16 }} />}
                  onClick={() => navigate('/consultations')}
                  sx={{
                    backgroundColor: axelionColors.gold,
                    color: '#FFFFFF',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    fontWeight: 500,
                    fontSize: '0.7rem',
                    px: 2,
                    py: 1,
                    borderRadius: '8px',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: axelionColors.goldDark,
                      boxShadow: 'none',
                    },
                  }}
                >
                  {t('dashboard.viewAll')}
                </Button>
              </Box>

              {consultations.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 6,
                    px: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 300,
                      fontSize: '1rem',
                      color: axelionColors.textDark,
                      mb: 1,
                    }}
                  >
                    {t('consultations.noConsultations')}
                  </Typography>
                  <Typography
                    sx={{
                      color: axelionColors.textMuted,
                      fontSize: '0.85rem',
                      mb: 3,
                    }}
                  >
                    {t('consultations.bookFirst')}
                  </Typography>
                  <Button
                    startIcon={<Search sx={{ fontSize: 16 }} />}
                    onClick={() => navigate('/lawyers')}
                    sx={{
                      backgroundColor: axelionColors.gold,
                      color: '#FFFFFF',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      px: 3,
                      py: 1.5,
                      borderRadius: '8px',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: axelionColors.goldDark,
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {t('dashboard.findLawyer')}
                  </Button>
                </Box>
              ) : isMobile ? (
                /* Mobile: Card layout */
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {consultations.map((consultation) => (
                    <Card
                      key={consultation.id}
                      sx={{
                        p: 2,
                        bgcolor: axelionColors.bgWarm,
                        border: `1px solid ${axelionColors.borderLight}`,
                        borderRadius: '8px',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: axelionColors.gold },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            backgroundColor: axelionColors.bgBeige,
                            color: axelionColors.gold,
                            fontSize: '0.85rem',
                            fontWeight: 300,
                          }}
                        >
                          {consultation.lawyerName?.charAt(0)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ color: axelionColors.textDark, fontSize: '0.9rem', fontWeight: 500 }}>
                            {consultation.lawyerName}
                          </Typography>
                          <Typography sx={{ color: axelionColors.textMuted, fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {consultation.topic || t('consultations.title')}
                          </Typography>
                        </Box>
                        <Chip
                          icon={consultation.type === 'video'
                            ? <VideoCall sx={{ fontSize: 14, color: axelionColors.gold }} />
                            : <Message sx={{ fontSize: 14, color: axelionColors.gold }} />
                          }
                          label={consultation.type === 'video' ? 'Видео' : 'Чат'}
                          size="small"
                          sx={{
                            backgroundColor: axelionColors.accentLight,
                            color: axelionColors.gold,
                            fontWeight: 500,
                            fontSize: '0.65rem',
                            borderRadius: '8px',
                            '& .MuiChip-icon': { color: axelionColors.gold },
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ color: axelionColors.textSecondary, fontSize: '0.8rem' }}>
                          {consultation.date} — {consultation.time}
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => handleJoinConsultation(consultation)}
                          sx={{
                            backgroundColor: axelionColors.gold,
                            color: '#FFFFFF',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            fontWeight: 500,
                            fontSize: '0.65rem',
                            px: 2,
                            py: 0.75,
                            borderRadius: '8px',
                            boxShadow: 'none',
                            '&:hover': {
                              backgroundColor: axelionColors.goldDark,
                              boxShadow: 'none',
                            },
                          }}
                        >
                          {t('consultations.joinCall')}
                        </Button>
                      </Box>
                    </Card>
                  ))}
                </Box>
              ) : (
                /* Desktop: Table layout */
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            color: axelionColors.textMuted,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            borderBottom: `1px solid ${axelionColors.borderLight}`,
                            backgroundColor: axelionColors.bgWarm,
                          }}
                        >
                          {t('consultations.lawyer')}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: axelionColors.textMuted,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            borderBottom: `1px solid ${axelionColors.borderLight}`,
                            backgroundColor: axelionColors.bgWarm,
                          }}
                        >
                          {t('consultations.topic')}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: axelionColors.textMuted,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            borderBottom: `1px solid ${axelionColors.borderLight}`,
                            backgroundColor: axelionColors.bgWarm,
                          }}
                        >
                          {t('consultations.date')}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: axelionColors.textMuted,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            borderBottom: `1px solid ${axelionColors.borderLight}`,
                            backgroundColor: axelionColors.bgWarm,
                          }}
                        >
                          {t('consultations.consultationType')}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: `1px solid ${axelionColors.borderLight}`,
                            backgroundColor: axelionColors.bgWarm,
                          }}
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {consultations.map((consultation) => (
                        <TableRow
                          key={consultation.id}
                          sx={{
                            '&:hover': {
                              backgroundColor: axelionColors.bgWarm,
                            },
                          }}
                        >
                          <TableCell sx={{ borderBottom: `1px solid ${axelionColors.borderLight}` }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  backgroundColor: axelionColors.bgBeige,
                                  color: axelionColors.gold,
                                  fontSize: '0.85rem',
                                  fontWeight: 300,
                                }}
                              >
                                {consultation.lawyerName?.charAt(0)}
                              </Avatar>
                              <Typography sx={{ color: axelionColors.textDark, fontSize: '0.9rem' }}>
                                {consultation.lawyerName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${axelionColors.borderLight}` }}>
                            <Typography sx={{ color: axelionColors.textSecondary, fontSize: '0.85rem' }}>
                              {consultation.topic || t('consultations.title')}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${axelionColors.borderLight}` }}>
                            <Typography sx={{ color: axelionColors.textSecondary, fontSize: '0.85rem' }}>
                              {consultation.date} - {consultation.time}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${axelionColors.borderLight}` }}>
                            <Chip
                              icon={consultation.type === 'video'
                                ? <VideoCall sx={{ fontSize: 14, color: axelionColors.gold }} />
                                : <Message sx={{ fontSize: 14, color: axelionColors.gold }} />
                              }
                              label={consultation.type === 'video' ? t('consultations.videoCall') : t('consultations.chatConsultation')}
                              size="small"
                              sx={{
                                backgroundColor: axelionColors.accentLight,
                                color: axelionColors.gold,
                                fontWeight: 500,
                                fontSize: '0.7rem',
                                letterSpacing: '0.05em',
                                borderRadius: '8px',
                                '& .MuiChip-icon': {
                                  color: axelionColors.gold,
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${axelionColors.borderLight}` }}>
                            <Button
                              size="small"
                              onClick={() => handleJoinConsultation(consultation)}
                              sx={{
                                backgroundColor: axelionColors.gold,
                                color: '#FFFFFF',
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                fontWeight: 500,
                                fontSize: '0.65rem',
                                px: 2,
                                py: 0.75,
                                borderRadius: '8px',
                                boxShadow: 'none',
                                '&:hover': {
                                  backgroundColor: axelionColors.goldDark,
                                  boxShadow: 'none',
                                },
                              }}
                            >
                              {t('consultations.joinCall')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Grid>

          {/* AI CTA removed — already in quick actions above */}
        </Grid>
      </Container>
    </Box>
  );
};

export default DashboardPageGlass;
